import type { SlotProps as MuiSlotProps, SnackbarProps, SxProps, Theme } from '@mui/material';
import type { FC } from 'react';
import type { ToastMessage } from './useToast';

import * as React from 'react';
import { Alert, AlertTitle, Box, Divider, IconButton, Snackbar, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { theme } from '@theguarantors/tg-package-material-ui';

export const DEFAULT_AUTO_HIDE_DURATION = 5000;

export type ToastStyle = Omit<
  SnackbarProps,
  'TransitionProps' | 'onClose' | 'open' | 'children' | 'message'
>;

export type ToastProps = {
  message: ToastMessage;
  onExited: () => void;
  snackbarSx?: SxProps<Theme>;
} & ToastStyle;

type SlotProps = MuiSlotProps<'div', any, any>;

// https://mui.com/material-ui/react-snackbar/#consecutive-snackbars
export const Toast: FC<ToastProps> = ({ message, onExited, autoHideDuration, ...props }) => {
  const {
    vertical = 'top',
    horizontal = 'right',
    sx = {},
    snackbarSx = {},
  } = message?.config || {};
  const [open, setOpen] = React.useState(true);
  const { sx: propsSx = {}, ...rest } = props;
  const mergedSx = { ...propsSx, ...sx } as SlotProps;

  const handleClose = (_event: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  return (
    <Snackbar
      key={message.key}
      open={open}
      onClose={handleClose}
      data-testid="toast-notification"
      TransitionProps={{ onExited }}
      anchorOrigin={{ vertical, horizontal }}
      autoHideDuration={autoHideDuration ?? DEFAULT_AUTO_HIDE_DURATION}
      sx={snackbarSx}
      {...rest}
    >
      <Alert
        severity={message.severity}
        onClose={message.cta ? undefined : handleClose}
        sx={mergedSx}
        action={
          message.cta ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              {message.cta}
              <IconButton aria-label="close" color="inherit" size="small" onClick={handleClose}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </Stack>
          ) : undefined
        }
      >
        <Stack direction="row" alignItems="center">
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {message.header ? <AlertTitle>{message.header}</AlertTitle> : null}
            {message.message}
          </Box>
          {message.cta ? (
            <Divider
              orientation="vertical"
              flexItem={true}
              sx={{ mx: '12px', my: '4px', bgcolor: theme.palette.neutralGrey?.[200] }}
            />
          ) : null}
        </Stack>
      </Alert>
    </Snackbar>
  );
};
